import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import AchievementImage from '../Achievements/AchievementImage';
import { getBadgeColor, getAchievementRarity } from '../../config/badgeColors';
import './BadgeSelector.scss';

const BadgeSelector = ({ userId, currentBadgeId, onSelect, onClose }) => {
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(currentBadgeId);

  useEffect(() => {
    fetchUserAchievements();
  }, [userId]);

  const fetchUserAchievements = async () => {
    try {
      const response = await apiService.get(`/api/achievements/user/${userId}`);
      const data = await response.json();
      
      // Sort achievements by rarity (highest first) then alphabetically
      const sortedAchievements = (data || []).sort((a, b) => {
        const rarityA = getAchievementRarity(a);
        const rarityB = getAchievementRarity(b);
        
        // Define rarity order (highest to lowest)
        const rarityOrder = { legendary: 4, epic: 3, rare: 2, common: 1 };
        const rarityValueA = rarityOrder[rarityA] || 0;
        const rarityValueB = rarityOrder[rarityB] || 0;
        
        // First sort by rarity (highest first)
        if (rarityValueA !== rarityValueB) {
          return rarityValueB - rarityValueA;
        }
        
        // Then sort alphabetically by name
        return a.name.localeCompare(b.name);
      });
      
      setAchievements(sortedAchievements);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (achievementId) => {
    setSelectedId(achievementId);
  };

  const handleSave = async () => {
    try {
      await apiService.put(`/auth/users/${userId}/badge`, { badge_achievement_id: selectedId });
      
      // Find the selected achievement object
      const selectedAchievement = achievements.find(ach => ach.id === selectedId);
      onSelect(selectedAchievement);
      onClose();
    } catch (error) {
      console.error('Error updating badge:', error);
      // Show user-friendly error message
      alert('Badge feature not available. Please contact administrator to run database migrations.');
    }
  };

  const handleRemoveBadge = async () => {
    try {
      await apiService.put(`/auth/users/${userId}/badge`, { badge_achievement_id: null });
      onSelect(null);
      onClose();
    } catch (error) {
      console.error('Error removing badge:', error);
      // Show user-friendly error message
      alert('Badge feature not available. Please contact administrator to run database migrations.');
    }
  };

  if (loading) {
    return (
      <div className="badge-selector__overlay" onClick={onClose}>
        <div className="badge-selector__modal" onClick={e => e.stopPropagation()}>
          <div className="badge-selector__loading">Loading achievements...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="badge-selector__overlay" onClick={onClose}>
      <div className="badge-selector__modal" onClick={e => e.stopPropagation()}>
        <h2 className="badge-selector__title">Select Your Badge</h2>
        <div className="badge-selector__list">
          {achievements.length === 0 ? (
            <p className="badge-selector__empty">No achievements unlocked yet</p>
          ) : (
            achievements.map(achievement => {
              const rarity = getAchievementRarity(achievement);
              const badgeColor = getBadgeColor(achievement);
              
              return (
                <div
                  key={achievement.id}
                  className={`badge-selector__item ${selectedId === achievement.id ? 'badge-selector__item--selected' : ''}`}
                  onClick={() => handleSelect(achievement.id)}
                >
                  <div className="badge-selector__item-left">
                    <AchievementImage 
                      filename={achievement.image_filename}
                      alt={achievement.name}
                      className="badge-selector__item-icon"
                    />
                    <span className="badge-selector__item-title">{achievement.name}</span>
                  </div>
                  <span 
                    className={`badge-selector__item-rarity badge-selector__item-rarity--${rarity}`}
                    style={{
                      '--rarity-color': badgeColor.background,
                      '--rarity-glow': badgeColor.glow
                    }}
                  >
                    {rarity.toUpperCase()}
                  </span>
                </div>
              );
            })
          )}
        </div>
        <div className="badge-selector__actions">
          <button className="badge-selector__button badge-selector__button--cancel" onClick={onClose}>
            Cancel
          </button>
          {currentBadgeId && (
            <button className="badge-selector__button badge-selector__button--remove" onClick={handleRemoveBadge}>
              Remove Badge
            </button>
          )}
          <button 
            className="badge-selector__button badge-selector__button--save" 
            onClick={handleSave}
            disabled={!selectedId}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeSelector;